import type { i18n } from 'i18next'
import yaml from 'js-yaml'
import { useMemo } from 'react'
import * as v from 'valibot'
import type { PromptStyle } from 'src/services/constants/preference'
import { promptStyleOptions } from 'src/services/constants/preference'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'
import localOnboardingYamlText from '../onboardingConfig.local.yaml?raw'

const i18nNamesSchema = v.objectWithRest(
  {
    en: v.string(),
  },
  v.string(),
)

export type OnboardingI18nNames = v.InferOutput<typeof i18nNamesSchema>

const characterInfoSchema = v.looseObject({
  id: v.string(),
  tackId: v.string(),
  tackCodeName: v.optional(v.string()),
  mediaId: v.optional(v.string()),
  names: i18nNamesSchema,
  promptTextNatural: v.optional(v.string()),
  promptTextNaturalJa: v.optional(v.string()),
  promptTextTags: v.optional(v.string()),
})

export type OnboardingCharacterInfo = v.InferOutput<typeof characterInfoSchema>

const onboardingDataSchema = v.looseObject({
  themes: v.record(v.string(), v.array(characterInfoSchema)),
  categoryNames: v.record(v.string(), i18nNamesSchema),
})

// Parsed once at module load. Mirrors the production payload from
// mewtant-dynamic-config so we can iterate on character/prompt entries
// without round-tripping through the config repo + Consul.
const localOnboardingData = (() => {
  try {
    return yaml.load(localOnboardingYamlText)
  } catch {
    return undefined
  }
})()

export function useOnboardingData() {
  const { data, isLoading } = usePublicDynamicConfig('onboarding', {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    fallbackData: localOnboardingData,
    // Dev source-of-truth is the bundled YAML. Skipping the mount-revalidate
    // keeps prompt edits visible without a network refetch clobbering them.
    revalidateOnMount: !import.meta.env.DEV,
  })
  const { output, success } = useMemo(
    () => v.safeParse(onboardingDataSchema, data),
    [data],
  )

  return {
    onboardingData: success ? output : undefined,
    isLoading,
  }
}

export function getOnboardingLocalizedName(
  names: OnboardingI18nNames,
  i18next: i18n,
) {
  for (const lang of i18next.languages) {
    if (names[lang]) {
      return names[lang]
    }
  }
  return names.en
}

export const ONBORADING_ACQUIRE_TASK_SCENE_ID =
  '3cc57cab-8e78-44f6-a5bd-2b6a58394b2c'

export function getPromptStyleOptionData() {
  return promptStyleOptions.map(promptStyle => ({
    name: promptStyle,
    labelI18nKey:
      `user:onboarding.prompt-style.option.${promptStyle}.title` as const,
    exampleI18nKey:
      `user:onboarding.prompt-style.option.${promptStyle}.example` as const,
  }))
}

export function getOnboardingFirstPrompt(
  character: OnboardingCharacterInfo,
  promptStyle: PromptStyle,
  i18next: i18n,
): string {
  if (promptStyle === 'natural-language') {
    // Japanese users get a natural Japanese prompt; English is the fallback.
    // Tag-mode is danbooru-tag based and locale-agnostic, so no JA variant.
    const isJa = i18next.languages.some(l => l === 'ja' || l.startsWith('ja-'))
    if (isJa && character.promptTextNaturalJa) {
      return character.promptTextNaturalJa
    }
    if (character.promptTextNatural) {
      return character.promptTextNatural
    }
  }
  if (promptStyle === 'precise-tags' && character.promptTextTags) {
    return character.promptTextTags
  }
  const name = getOnboardingLocalizedName(character.names, i18next)
  return promptStyle === 'precise-tags'
    ? `1girl, ${name}, masterpiece, best quality`
    : `Anime illustration of ${name}, masterpiece, best quality`
}
